import React, { useCallback, useMemo, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import CampaignIcon from '@mui/icons-material/Campaign';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';

import { createDefaultState, withDefaultSuccessSteps } from '~/utils/societePayloadBuilder/statusCampaign/defaults';
import { hasFormProgress } from '~/utils/societePayloadBuilder/statusCampaign/hasFormProgress';
import { parsePayloadJson } from '~/utils/societePayloadBuilder/statusCampaign/parsePayload';
import type {
  CampaignMode,
  StatusCampaignPayloadBuilderState,
} from '~/utils/societePayloadBuilder/statusCampaign/types';
import { validatePayloadState } from '~/utils/societePayloadBuilder/statusCampaign/validatePayload';

import OffersHubPayloadBuilderPage from './PayloadBuilder/OffersHub/OffersHubPayloadBuilderPage';
import { PAYLOAD_BUILDER_ALERT_BUTTON_SX } from './PayloadBuilder/PayloadBuilderConstants';
import PayloadBuilderSetupGuide from './PayloadBuilder/PayloadBuilderSetupGuide';
import PayloadBuilderToolbar, {
  type PayloadBuilderMode,
  isStandalonePayloadMode,
} from './PayloadBuilder/PayloadBuilderToolbar';
import {
  PayloadBuilderToolbarActionsProvider,
  usePayloadBuilderToolbarActionsState,
} from './PayloadBuilder/PayloadBuilderToolbarActions';
import SpendPointsPayloadBuilderPage from './PayloadBuilder/SpendPoints/SpendPointsPayloadBuilderPage';
import StatusCampaignPayloadActions from './PayloadBuilder/StatusCampaign/StatusCampaignPayloadActions';
import StatusCampaignPayloadBuilderForm from './PayloadBuilder/StatusCampaign/StatusCampaignPayloadBuilderForm';
import StatusCampaignPayloadPreview from './PayloadBuilder/StatusCampaign/StatusCampaignPayloadPreview';
import WhatsNewCarouselPayloadBuilderPage from './PayloadBuilder/WhatsNewCarousel/WhatsNewCarouselPayloadBuilderPage';

function isCampaignMode(mode: PayloadBuilderMode): mode is CampaignMode {
  return mode === 'statusMatch' || mode === 'statusClaim';
}

const WORKSPACE_MAX_HEIGHT = 'calc(100vh - 300px)';

const MODE_LABELS: Record<PayloadBuilderMode, string> = {
  statusMatch: 'Status Match',
  statusClaim: 'Status Claim',
  offersHub: 'Offers Hub',
  whatsNewCarousel: "What's New Carousel",
  spendPoints: 'Spend Points',
};

const ALERT_COPY: Record<PayloadBuilderMode, { title: string; description: string }> = {
  statusMatch: {
    title: 'Campaign payload builder',
    description:
      'Build JSON configs for Société Status Match or Status Claim Lab feature flags. Use the live preview to copy or download the payload, then paste it into the Lab flag configuration.',
  },
  statusClaim: {
    title: 'Campaign payload builder',
    description:
      'Build JSON configs for Société Status Match or Status Claim Lab feature flags. Use the live preview to copy or download the payload, then paste it into the Lab flag configuration.',
  },
  offersHub: {
    title: 'Offers Hub payload builder',
    description:
      'Build JSON configs for the Société Offers Hub page. Use the live preview to copy or download the payload, then paste it into the Lab flag configuration.',
  },
  whatsNewCarousel: {
    title: "What's New Carousel payload builder",
    description:
      "Build JSON configs for the Société What's New carousel. Use the live preview to copy or download the payload, then paste it into the Lab flag configuration.",
  },
  spendPoints: {
    title: 'Spend Points payload builder',
    description:
      'Build JSON configs for the Société Spend Points page. Use the live preview to copy or download the payload, then paste it into the Lab flag configuration.',
  },
};

export default function LoyaltyPayloadBuilderPage() {
  const theme = useTheme();
  const borderSoft = alpha(theme.palette.divider, 0.12);
  const { toolbarActions, setToolbarActions } = usePayloadBuilderToolbarActionsState();

  const [selectedMode, setSelectedMode] = useState<PayloadBuilderMode>('statusMatch');
  const [state, setState] = useState<StatusCampaignPayloadBuilderState>(() => createDefaultState('statusMatch'));
  const [loadJson, setLoadJson] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadCollapseKey, setLoadCollapseKey] = useState(0);
  const [pendingMode, setPendingMode] = useState<PayloadBuilderMode | null>(null);
  const [isModeConfirmOpen, setIsModeConfirmOpen] = useState(false);
  const [offersHubHasProgress, setOffersHubHasProgress] = useState(false);
  const [whatsNewCarouselHasProgress, setWhatsNewCarouselHasProgress] = useState(false);
  const [spendPointsHasProgress, setSpendPointsHasProgress] = useState(false);
  const validation = useMemo(() => validatePayloadState(state), [state]);

  const applyModeChange = useCallback(
    (mode: PayloadBuilderMode) => {
      if (isStandalonePayloadMode(mode)) {
        setSelectedMode(mode);
        setState((currentState) => createDefaultState(currentState.mode));
      } else if (isCampaignMode(mode)) {
        const next = createDefaultState(mode);
        setSelectedMode(mode);
        setState(mode === 'statusMatch' ? withDefaultSuccessSteps(next) : next);
      }
      setLoadJson('');
      setLoadError(null);
      setOffersHubHasProgress(false);
      setWhatsNewCarouselHasProgress(false);
      setSpendPointsHasProgress(false);
      setToolbarActions(null);
    },
    [setToolbarActions],
  );

  const handleRequestModeChange = useCallback(
    (mode: PayloadBuilderMode) => {
      if (mode === selectedMode) return;

      const hasCurrentModeProgress =
        selectedMode === 'offersHub'
          ? offersHubHasProgress
          : selectedMode === 'whatsNewCarousel'
            ? whatsNewCarouselHasProgress
            : selectedMode === 'spendPoints'
              ? spendPointsHasProgress
              : hasFormProgress(state, loadJson);

      if (hasCurrentModeProgress) {
        setPendingMode(mode);
        setIsModeConfirmOpen(true);
        return;
      }
      applyModeChange(mode);
    },
    [
      applyModeChange,
      loadJson,
      offersHubHasProgress,
      selectedMode,
      spendPointsHasProgress,
      state,
      whatsNewCarouselHasProgress,
    ],
  );

  const handleConfirmModeChange = useCallback(() => {
    if (pendingMode) {
      applyModeChange(pendingMode);
    }
    setPendingMode(null);
    setIsModeConfirmOpen(false);
  }, [applyModeChange, pendingMode]);

  const handleCancelModeChange = useCallback(() => {
    setPendingMode(null);
    setIsModeConfirmOpen(false);
  }, []);

  const applyJson = useCallback(
    (text: string) => {
      if (!isCampaignMode(selectedMode)) {
        return;
      }

      try {
        const nextState = parsePayloadJson(text, selectedMode);
        setState(nextState);
        setLoadCollapseKey((value) => value + 1);
        setLoadError(null);
      } catch (error) {
        setLoadError(error instanceof Error ? error.message : 'Invalid JSON');
      }
    },
    [selectedMode],
  );

  const handleLoadPayload = useCallback(() => {
    applyJson(loadJson);
  }, [applyJson, loadJson]);

  const handleUploadFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const text = typeof reader.result === 'string' ? reader.result : '';
        setLoadJson(text);
        applyJson(text);
      };
      reader.readAsText(file);
    },
    [applyJson],
  );

  const panelSx = {
    borderRadius: 2,
    border: `1px solid ${borderSoft}`,
    maxHeight: WORKSPACE_MAX_HEIGHT,
    overflow: 'auto',
    boxSizing: 'border-box',
  } as const;
  const alertCopy = ALERT_COPY[selectedMode];

  return (
    <PayloadBuilderToolbarActionsProvider onActionsChange={setToolbarActions}>
      <Box>
        <Alert icon={<CampaignIcon />} severity="info" sx={{ mb: 3 }}>
          <AlertTitle>{alertCopy.title}</AlertTitle>
          <Box>{alertCopy.description}</Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 1.5 }}
          >
            <PayloadBuilderSetupGuide selectedMode={selectedMode} />
            <Button
              component={Link}
              to="/lab"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="outlined"
              color="info"
              startIcon={<OpenInNewIcon fontSize="small" />}
              sx={PAYLOAD_BUILDER_ALERT_BUTTON_SX}
            >
              Open Lab
            </Button>
          </Stack>
        </Alert>

        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar - 1,
            mb: 2,
            bgcolor: 'background.default',
          }}
        >
          <PayloadBuilderToolbar
            selectedMode={selectedMode}
            state={state}
            isValid={validation.isValid}
            standaloneActions={toolbarActions}
            onRequestModeChange={handleRequestModeChange}
          />
        </Box>

        <Box sx={{ display: isStandalonePayloadMode(selectedMode) ? 'none' : 'block' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 5.4 }}>
              <Paper elevation={0} sx={{ ...panelSx, p: 1.75 }}>
                <StatusCampaignPayloadBuilderForm
                  state={state}
                  onChange={setState}
                  loadJson={loadJson}
                  onLoadJsonChange={setLoadJson}
                  loadError={loadError}
                  loadCollapseKey={loadCollapseKey}
                  onLoadPayload={handleLoadPayload}
                  onClearLoad={() => {
                    setLoadJson('');
                    setLoadError(null);
                  }}
                  onUploadFile={handleUploadFile}
                />
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6.6 }}>
              <Box
                sx={{
                  display: { xs: 'block', md: 'none' },
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${borderSoft}`,
                  bgcolor: 'background.paper',
                }}
              >
                <StatusCampaignPayloadActions state={state} isValid={validation.isValid} fullWidth />
              </Box>
              <StatusCampaignPayloadPreview state={state} borderSoft={borderSoft} maxHeight={WORKSPACE_MAX_HEIGHT} />
            </Grid>
          </Grid>
        </Box>

        {selectedMode === 'offersHub' ? (
          <OffersHubPayloadBuilderPage onProgressChange={setOffersHubHasProgress} />
        ) : null}
        {selectedMode === 'whatsNewCarousel' ? (
          <WhatsNewCarouselPayloadBuilderPage onProgressChange={setWhatsNewCarouselHasProgress} />
        ) : null}
        {selectedMode === 'spendPoints' ? (
          <SpendPointsPayloadBuilderPage onProgressChange={setSpendPointsHasProgress} />
        ) : null}

        <Dialog open={isModeConfirmOpen} onClose={handleCancelModeChange} maxWidth="xs" fullWidth>
          <DialogTitle>Switch payload type?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Switching from {MODE_LABELS[selectedMode]} to {pendingMode ? MODE_LABELS[pendingMode] : 'another type'}{' '}
              will clear all fields you have entered. This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelModeChange}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={handleConfirmModeChange}>
              Switch payload type
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PayloadBuilderToolbarActionsProvider>
  );
}
